import { motion } from 'framer-motion'
import { Heart, CheckCircle, ArrowRight, MapPin } from 'lucide-react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

const benefits = [
  { title: 'Inner Conflict Resolution', body: 'Stop fighting yourself. IFS helps you understand why different parts pull in opposite directions — and how to bring them into agreement.' },
  { title: 'Self-Leadership', body: 'Build the capacity to respond rather than react. Lead your life from your core Self rather than from fear, habit, or other people\'s expectations.' },
  { title: 'Clarity Under Pressure', body: 'When the stakes are high, parts can take over. IFS gives you tools to stay grounded and clear-headed when it matters most.' },
  { title: 'Sustainable Momentum', body: 'Stop white-knuckling your way forward. When your parts feel heard, motivation becomes natural — not something you have to manufacture.' },
]

const steps = [
  { n: '01', label: 'Free Consultation', body: 'A no-pressure 30-minute conversation. We talk about what you\'re navigating and whether IFS coaching is the right fit.' },
  { n: '02', label: 'Mapping Your Parts', body: 'We identify the key inner voices currently running the show — the achiever, the protector, the inner critic, the one who wants out.' },
  { n: '03', label: 'Working the System', body: 'We engage each part directly, unblend them from your core Self, and update the stories they\'ve been carrying.' },
  { n: '04', label: 'Forward Motion', body: 'With your inner system more aligned, decisions get clearer. Energy consolidates. Progress stops feeling like a battle.' },
]

export default function IFSCoachingCAPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            <MapPin size={14} /> California · Virtual
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            IFS Coaching<br className="hidden sm:block" /> in California
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">
            Internal Family Systems coaching for California professionals ready to stop fighting themselves and start leading from within.
          </motion.p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        <div className="max-w-3xl mb-16">
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-5">
            Most people who come to IFS coaching aren't broken. They're capable, often high-achieving — and exhausted from managing competing voices inside themselves.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}
            className="text-slate-600 dark:text-slate-400 leading-relaxed">
            IFS (Internal Family Systems) is a proven framework developed by Dr. Richard Schwartz. In coaching, we use it not to dig into your past, but to understand what's running your present — and give you tools to lead from your wisest, most grounded Self.
          </motion.p>
        </div>

        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8">What IFS Coaching Addresses</h2>
        <div className="grid sm:grid-cols-2 gap-5 mb-20">
          {benefits.map(({ title, body }, i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="p-6 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700">
              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-4">
                <Heart size={15} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{title}</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>

        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8">How It Works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {steps.map(({ n, label, body }, i) => (
            <motion.div key={n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="relative p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
              <span className="text-4xl font-bold text-slate-100 dark:text-slate-700 absolute top-4 right-4">{n}</span>
              <div className="relative">
                <CheckCircle size={16} className="text-blue-500 mb-3" />
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2 text-sm">{label}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed">{body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-slate-900 rounded-3xl p-10 text-center">
          <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">California · Virtual Sessions</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Ready to work with your parts, not against them?</h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-8 leading-relaxed">
            Start with a free 30-minute consultation. No pressure, no sales pitch — just a real conversation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
              Book Free Consultation
            </CalendlyBtn>
            <a href="/coaching-vs-therapy" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm transition-colors">
              Coaching vs. Therapy <ArrowRight size={13} />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
